import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useMotionValue, useSpring } from 'framer-motion';

const ScrollContext = createContext(null);

export const useScrollContext = () => {
  const context = useContext(ScrollContext);
  if (!context) {
    throw new Error('useScrollContext must be used within a ScrollProvider');
  }
  return context;
};

export const ScrollProvider = ({ children, rootMargin = '0px 0px -20% 0px', threshold = 0.1 }) => {
  const [activeBeats, setActiveBeats] = useState(new Set());
  const observerRef = useRef(null);
  const elementsRef = useRef(new Map());

  // Shared scroll progress (0-1) for all scroll-linked animations
  const scrollYProgress = useMotionValue(0);
  const scrollYProgressSpring = useSpring(scrollYProgress, { bounce: 0, duration: 0.4 });

  // Register a beat element with the observer
  const registerBeat = useCallback((beatId, element) => {
    if (!element || !beatId) return;
    elementsRef.current.set(beatId, element);
    if (observerRef.current) {
      observerRef.current.observe(element);
    }
  }, []);

  // Unregister a beat element
  const unregisterBeat = useCallback((beatId) => {
    const element = elementsRef.current.get(beatId);
    if (element && observerRef.current) {
      observerRef.current.unobserve(element);
    }
    elementsRef.current.delete(beatId);
  }, []);

  // Single IntersectionObserver for all beat elements
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const beatId = entry.target.dataset.beatId;
          if (!beatId) return;

          if (entry.isIntersecting) {
            setActiveBeats(prev => {
              const next = new Set(prev);
              next.add(beatId);
              return next;
            });
          }
        });
      },
      { rootMargin, threshold }
    );

    // Observe all currently registered elements
    elementsRef.current.forEach((element) => {
      observerRef.current.observe(element);
    });

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [rootMargin, threshold]);

  // Single scroll listener for all scroll-linked transforms
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? Math.min(scrollY / docHeight, 1) : 0;
      scrollYProgress.set(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Initial value
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrollYProgress]);

  const isBeatActive = useCallback((beatId) => activeBeats.has(beatId), [activeBeats]);
  const isBeatCompleted = useCallback((beatId) => activeBeats.has(beatId), [activeBeats]);

  const value = {
    activeBeats,
    isBeatActive,
    isBeatCompleted,
    registerBeat,
    unregisterBeat,
    scrollYProgress,
    scrollYProgressSpring,
  };

  return (
    <ScrollContext.Provider value={value}>
      {children}
    </ScrollContext.Provider>
  );
};

export default ScrollContext;